import React, { useState, useEffect } from 'react';
import { Select, Button, Alert, Spin, Switch, message } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import intl from 'react-intl-universal';
import { UserIntlStorage, getCurrentIntlConfig } from '@/utils/intl';
import { updateUserIntlConfig, getSysTimeZoneList, getSysLanguageList } from '@/api/login';
import styles from './index.module.scss';

interface FormValue {
  Language: string; // 语言
  TimeZone: number; // 时区
  DSTStatus: boolean; // 夏令时
}

const LanguageTimeZone: React.FC = () => {
  // 状态管理
  const [loading, setLoading] = useState<boolean>(false);
  const [languageList, setLanguageList] = useState<any[]>([]);
  const [timezoneList, setTimezoneList] = useState<any[]>([]);
  const [formValue, setFormValue] = useState<FormValue>({
    Language: '',
    TimeZone: 0,
    DSTStatus: false,
  });
  const [cacheValue, setCacheValue] = useState<FormValue | null>(null);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);

  // 语言列表
  const getLanguageList = () => {
    return getSysLanguageList()
      .then((res) => {
        if (res && res.data) {
          return res.data.map((item) => ({
            label: item.localName,
            value: item.language,
            detail: item,
          }));
        }
        return [];
      })
      .catch(() => []);
  };

  // 时区列表
  const getTimeZoneList = (language) => {
    return getSysTimeZoneList({ language })
      .then((res) => {
        if (res && res.data) {
          return res.data.map((item) => ({
            label: `${item.timeZoneCode} ${item.region}`,
            value: item.timeZoneId,
            detail: item,
          }));
        }
        return [];
      })
      .catch(() => []);
  };

  // 更新时区列表
  const updateTimeZoneList = async (language) => {
    setLoading(true);
    try {
      const tzList = await getTimeZoneList(language);
      setTimezoneList(tzList);
    } catch (error) {
      console.error('获取时区列表失败', error);
    }
    setLoading(false);
  };

  // 检查表单是否有变化
  const checkFormChanged = (currentValue: FormValue) => {
    if (!cacheValue) return false;
    return Object.keys(currentValue).some((key) => currentValue[key] !== cacheValue[key]);
  };

  // 加载用户设置
  useEffect(() => {
    const loadUserSettings = async () => {
      setLoading(true);
      try {
        const currentConfig = await getCurrentIntlConfig();
        const [langList, tzList] = await Promise.all([
          getLanguageList(),
          getTimeZoneList(currentConfig.Language),
        ]);

        setLanguageList(langList);
        setTimezoneList(tzList);
        setCacheValue(currentConfig);

        if (currentConfig) {
          const { Language, TimeZone, DSTStatus } = currentConfig;
          const newFormValue = {
            Language: langList.some((item) => item.value === Language) ? Language : '',
            TimeZone: tzList.some((item) => item.value === TimeZone) ? TimeZone : 0,
            DSTStatus: DSTStatus || false,
          };
          setFormValue(newFormValue);
          setIsFormChanged(checkFormChanged(newFormValue));
        }
      } catch (error) {
        console.error('加载用户设置失败', error);
      }
      setLoading(false);
    };

    loadUserSettings();
  }, []);

  // 修改语言
  const handleLanguageChange = (value) => {
    const newFormValue = { ...formValue, Language: value };
    setFormValue(newFormValue);
    setIsFormChanged(checkFormChanged(newFormValue));
    // updateTimeZoneList(value);
  };

  // 修改时区
  const handleTimezoneChange = (value) => {
    const newFormValue = { ...formValue, TimeZone: value };
    setFormValue(newFormValue);
    setIsFormChanged(checkFormChanged(newFormValue));
  };

  // 修改夏令时
  const handleDSTChange = (checked) => {
    const newFormValue = { ...formValue, DSTStatus: checked };
    setFormValue(newFormValue);
    setIsFormChanged(checkFormChanged(newFormValue));
  };

  // 应用设置
  const handleApply = async () => {
    if (!formValue.Language || !formValue.TimeZone) {
      message.error(intl.get('app_custom_quick_entry_select_language_and_timezone'));
      return;
    }

    setLoading(true);
    try {
      const valueKeys = ['Language', 'TimeZone', 'DSTStatus'];
      const currentValue = cacheValue || {};

      // 数据有变更继续操作
      if (
        formValue.Language &&
        formValue.TimeZone &&
        valueKeys.some((fieldKey) => currentValue[fieldKey] !== formValue[fieldKey])
      ) {
        const { Language, TimeZone, DSTStatus } = formValue;
        const timeItem = timezoneList.find((item) => item.value === TimeZone);
        const { timeZoneCode } = timeItem.detail;

        await updateUserIntlConfig({
          daylightTimeFlag: DSTStatus,
          language: Language,
          timeZoneCode,
          timeZoneId: TimeZone,
          scene: 'FE_MANUAL_UPDATE',
        });

        UserIntlStorage.setLocalConfig({
          Language,
          TimeZone,
          TimeZoneCode: timeZoneCode,
          DSTStatus,
        });

        window.location.reload();
      } else {
        message.info('设置未变更');
      }
    } catch (error) {
      console.error('应用设置失败', error);
    }
    setLoading(false);
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.container}>
        <div className={styles.header}>
          {intl.get('app_custom_quick_entry_language_and_timezone')}
        </div>
        <Alert
          icon={<InfoCircleFilled />}
          message={intl.get('app_custom_quick_entry_language_and_timezone_tips')}
          type="info"
          showIcon
          className={styles.alert}
        />

        <div className={styles.settingItem}>
          <div className={styles.label}>{intl.get('app_custom_quick_entry_language')}</div>
          <div className={styles.control}>
            <Select
              value={formValue.Language}
              onChange={handleLanguageChange}
              style={{ width: '100%' }}
              options={languageList}
              dropdownMatchSelectWidth={false}
              placeholder={intl.get('app_custom_quick_entry_select_language')}
            />
          </div>
        </div>

        <div className={styles.settingItem}>
          <div className={styles.label}>{intl.get('app_custom_quick_entry_timezone')}</div>
          <div className={styles.control}>
            <Select
              disabled
              value={formValue.TimeZone}
              onChange={handleTimezoneChange}
              style={{ width: '100%' }}
              options={timezoneList}
              dropdownMatchSelectWidth={false}
              placeholder={intl.get('app_custom_quick_entry_select_timezone')}
            />
          </div>
        </div>

        <div className={styles.settingItem}>
          <div className={styles.label}>{intl.get('app_custom_quick_entry_summer_time')}</div>
          <div className={styles.control}>
            <Switch
              disabled
              checked={formValue.DSTStatus}
              onChange={handleDSTChange}
              checkedChildren={intl.get('app_open')}
              unCheckedChildren={intl.get('app_confirm_close_btn')}
            />
          </div>
        </div>

        <div className={styles.footer}>
          <Button type="primary" onClick={handleApply} loading={loading} disabled={!isFormChanged}>
            {intl.get('app_custom_quick_entry_apply')}
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default LanguageTimeZone;
